/**
 * CLP Content Spot — Web Component
 *
 * <clp-content-spot> has three responsibilities:
 *
 * 1. Grid injection (teleportation):
 *    CLP blocks render via content_for 'blocks' (below the product grid) so
 *    the Theme Editor can interact with them. On connect, the element detects
 *    it's in the .section-resource-list__content area and moves itself into the
 *    .resource-list--grid at the position specified by data-placement.
 *    Without JS the element stays hidden in the blocks area (progressive
 *    enhancement — the product grid still works perfectly).
 *
 * 2. Vertically-centred sticky (data-sticky-mode="centered"):
 *    Sets --clp-sticky-top so the .clp-content-spot__sticky inner element stays
 *    vertically centred in the viewport as the user scrolls through the module.
 *    Recalculates on resize. Degrades to sticky-to-top without this code path.
 */
class ClpContentSpot extends HTMLElement {
  connectedCallback() {
    if (this.closest('.resource-list, [data-testid="product-grid"]')) {
      // Element is in a product grid — initialise behaviours.
      this.#initSticky();
    } else if (this.closest('[data-clp-enabled]')) {
      // Element is in the section but outside the grid — teleport into the grid.
      this.#teleportToGrid();
    }
  }

  // ── Grid injection ──────────────────────────────────────────────────────

  #teleportToGrid() {
    // Visibility gate: respect the collection-level CLP flag set on the section.
    const section = /** @type {HTMLElement | null} */ (this.closest('[data-clp-enabled]'));
    if (section?.dataset.clpEnabled === 'false') return;

    // Support both product-list (.resource-list--grid) and main-collection (.product-grid).
    const grid = section?.querySelector('.resource-list--grid, [data-testid="product-grid"]');
    if (!grid) return;

    // Teleport the Shopify block wrapper (.shopify-block) rather than the bare
    // element, so the Theme Editor's block binding/selection stays intact.
    // Copy --clp-span onto the wrapper so the grid-span CSS resolves there.
    const node = /** @type {HTMLElement} */ (this.closest('.shopify-block') || this);
    const span = getComputedStyle(this).getPropertyValue('--clp-span').trim();
    if (span) node.style.setProperty('--clp-span', span);
    node.classList.add('clp-grid-item');
    if (this.dataset.fullWidth === 'true') node.classList.add('clp-grid-item--full');

    this._clpNode = node;
    this._clpGrid = grid;
    this.#placeInGrid();

    // Column count is responsive (auto-fill grid), so recompute placement when
    // the viewport changes — a row-based placement must follow the breakpoint.
    if (!this._clpResizeBound) {
      this._clpResizeBound = true;
      /** @type {ReturnType<typeof setTimeout> | undefined} */
      let t;
      window.addEventListener('resize', () => {
        clearTimeout(t);
        t = setTimeout(() => this.#placeInGrid(), 150);
      });
    }
    // Moving the wrapper disconnects/reconnects this element → connectedCallback
    // fires again and hits the in-grid branch (initSticky/initVideoControls).
  }

  // Public entry point so the results-list view switcher can trigger
  // re-placement when the column count changes without a resize event.
  placeInGrid() {
    this.#placeInGrid();
  }

  // Place (or re-place) the teleported wrapper at its target product position.
  // placement_row (preferred) is multiplied by the detected desktop column
  // count so "after row 3" lands after 3 full rows of products regardless of
  // how many columns the responsive grid currently shows. Row 0 is a distinct
  // case — insert before the first product, at the very top of the grid.
  #placeInGrid() {
    const grid = this._clpGrid;
    const node = this._clpNode;
    if (!grid || !node) return;

    // Hide the wrapper before measuring so it doesn't push tiles into new rows
    // and distort the offsetTop-based column count (common when re-placing after
    // a view switch where the column count has changed since last placement).
    node.style.display = 'none';

    const tiles = /** @type {HTMLElement[]} */ ([...grid.querySelectorAll('.resource-list__item, .product-grid__item')]);
    if (!tiles.length) {
      node.style.display = '';
      return;
    }

    // Detect columns = number of tiles sharing the first tile's top offset.
    const firstTile = tiles[0];
    if (!firstTile) {
      node.style.display = '';
      return;
    }
    const firstTop = firstTile.offsetTop;
    let columns = 0;
    for (const tile of tiles) {
      if (Math.abs(tile.offsetTop - firstTop) < 4) columns++;
      else break;
    }
    columns = columns || 1;

    const min = parseInt(this.dataset.placementMin ?? '', 10) || 0;

    // Separate desktop/mobile row positions. Mobile (<722px, matching the CSS
    // breakpoint) uses placement_row_mobile when explicitly set, else the
    // desktop row. Row 0 is a valid, explicit choice — "insert before the
    // first product" — so attribute *presence* (not its value) decides which
    // row wins; a missing attribute falls through to the legacy tile-index
    // placement below.
    const isMobile = window.matchMedia('(max-width: 721px)').matches;
    const mobileRowAttr = this.dataset.placementRowMobile;
    const desktopRowAttr = this.dataset.placementRow;
    const rowAttr = isMobile && mobileRowAttr !== undefined ? mobileRowAttr : desktopRowAttr;
    const row = rowAttr !== undefined ? parseInt(rowAttr, 10) : NaN;

    let target;
    let insertBeforeFirst = false;

    if (Number.isNaN(row)) {
      let afterTile = parseInt(this.dataset.placement ?? '', 10) || 4;
      afterTile = Math.min(Math.max(afterTile, min || 1), tiles.length);
      target = tiles[afterTile - 1];
    } else if (row <= 0) {
      insertBeforeFirst = true;
    } else {
      const afterTile = Math.min(Math.max(row * columns, min || 1), tiles.length);
      target = tiles[afterTile - 1];
    }

    if (insertBeforeFirst) {
      if (firstTile.previousElementSibling !== node) {
        firstTile.insertAdjacentElement('beforebegin', node);
      }
    } else if (target && target.nextElementSibling !== node) {
      target.insertAdjacentElement('afterend', node);
    }

    node.style.display = '';
  }

  // ── Sticky ──────────────────────────────────────────────────────────────

  #initSticky() {
    if (this._stickyInit) return; // moving the node re-fires connectedCallback
    this._stickyInit = true;
    if (this.dataset.stickyMode === 'centered') {
      this.#initCenteredSticky();
    }
  }

  #initCenteredSticky() {
    const sticky = /** @type {HTMLElement | null} */ (this.querySelector('.clp-content-spot__sticky'));
    if (!sticky) return;

    const update = () => {
      const offset = (window.innerHeight - sticky.offsetHeight) / 2;
      this.style.setProperty('--clp-sticky-top', Math.max(0, offset) + 'px');
    };

    const ro = new ResizeObserver(update);
    ro.observe(sticky);
    ro.observe(document.documentElement);
    update();
  }

}

customElements.define('clp-content-spot', ClpContentSpot);
